import { useMemo } from 'react';

import { prepareNode } from 'features/cluster/nodes/NodeList/utils';

import { Cluster } from 'types/cluster';
import { AdditionalInformationSection } from './_components/AdditionalInformationSection';

import { CloudSection } from './_components/CloudSection';
import { CostSection } from './_components/CostSection';
import { DetailsSection } from './_components/DetailsSection';
import { OverviewSection } from './_components/OverviewSection';
import { UtilizationSection } from './_components/UtilizationSection';
import { NodeProblemsAlert } from '../../../_components/NodeProblemsAlert';
import { NodeWithDependencies } from '../../../types';
import { NodeMetrics } from '../../../utils/getNodeMetrics';
import { Box, Stack } from '@cast/design-system';
import { useCluster } from '@cast/shared-cluster-context-data-access';

type Props = {
  node: NodeWithDependencies;
  cluster: Cluster;
  nodeMetrics: NodeMetrics;
};

const OverviewTabContentLayout = ({
  overviewSection,
  costSection,
  cloudSection,
  detailsSection,
  additionalInformationSection,
  utilizationSection,
}: {
  overviewSection: React.ReactNode;
  costSection: React.ReactNode;
  cloudSection: React.ReactNode;
  detailsSection: React.ReactNode;
  additionalInformationSection: React.ReactNode;
  utilizationSection?: React.ReactNode;
}) => {
  return (
    <Stack gap={16}>
      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        gap={24}
        overflow="auto"
      >
        <Stack gap={16} sx={{ minWidth: 340 }}>
          {overviewSection}
        </Stack>
        <Stack gap={16} sx={{ minWidth: 340 }}>
          {cloudSection}
          {detailsSection}
          {costSection}
        </Stack>
        <Box sx={{ minWidth: 340 }}>{additionalInformationSection}</Box>
      </Box>
      {utilizationSection}
    </Stack>
  );
};

export const OverviewTabContent = ({ node, cluster, nodeMetrics }: Props) => {
  const { isCastAnywhere } = useCluster();
  const preparedNode = useMemo(
    () => prepareNode(node, undefined, isCastAnywhere),
    [node, isCastAnywhere]
  );
  const { cpuMetrics, memoryMetrics, ephemeralStorageMetrics } = nodeMetrics;

  return (
    <Stack gap={16}>
      <NodeProblemsAlert node={node} shouldOpenWorkloadsInNewTab={false} />
      <OverviewTabContentLayout
        overviewSection={<OverviewSection node={preparedNode} />}
        costSection={<CostSection node={preparedNode} />}
        cloudSection={<CloudSection node={preparedNode} />}
        detailsSection={<DetailsSection node={preparedNode} />}
        additionalInformationSection={
          <AdditionalInformationSection node={preparedNode} cluster={cluster} />
        }
        utilizationSection={
          <UtilizationSection
            cpuMetrics={cpuMetrics}
            memoryMetrics={memoryMetrics}
            ephemeralStorageMetrics={ephemeralStorageMetrics}
          />
        }
      />
    </Stack>
  );
};

const OverviewTabContentSkeleton = () => {
  return (
    <Stack gap={16}>
      <OverviewTabContentLayout
        overviewSection={<OverviewSection.Skeleton />}
        costSection={<CostSection.Skeleton />}
        cloudSection={<CloudSection.Skeleton />}
        detailsSection={<DetailsSection.Skeleton />}
        additionalInformationSection={<AdditionalInformationSection.Skeleton />}
        utilizationSection={<UtilizationSection.Skeleton />}
      />
    </Stack>
  );
};

OverviewTabContent.Skeleton = OverviewTabContentSkeleton;
